import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api"; // adjust if your api helper path differs
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  Switch,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MapIcon from "@mui/icons-material/Map";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ShieldIcon from "@mui/icons-material/Shield";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function SectionRule() {
  const theme = useTheme();
  const line = theme.palette.mode === "dark" ? "rgba(255,255,255,0.22)" : "rgba(15, 23, 42, 0.12)";
  const glow = theme.palette.mode === "dark" ? "rgba(96, 165, 250, 0.55)" : "rgba(37, 99, 235, 0.35)";
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 0,
        my: -1.5,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: "1px",
          background: `linear-gradient(90deg, transparent, ${line} 16%, ${glow} 50%, ${line} 84%, transparent)`,
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 0,
          width: 5,
          height: 5,
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "primary.main",
          opacity: 0.7,
          boxShadow: `0 0 0 4px ${theme.palette.background.paper}`,
        }}
      />
    </Box>
  );
}

// Reuse the same SettingRow pattern used elsewhere in Settings
function SettingRow({ label, description, checked, onChange, disabled }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1.5,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box sx={{ minWidth: 0, pr: 1.5 }}>
        <Typography fontWeight={600}>{label}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}

function StatusChip({ enabled, configured = true }) {
  const label = enabled ? "Enabled" : configured ? "Disabled" : "Not configured";
  const color = enabled ? "success" : "default";

  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={enabled ? "filled" : "outlined"}
      sx={{
        height: 20,
        fontSize: "0.66rem",
        fontWeight: 700,
      }}
    />
  );
}

/** CompanyProfile pref: null/undefined = automatic (on when env reports configured). */
function integrationToggleFromPref(pref, envConfigured) {
  if (pref === false) return false;
  if (pref === true) return true;
  return !!envConfigured;
}

let adtRowSeq = 0;
function newAdtRowId() {
  adtRowSeq += 1;
  return `adt-${Date.now()}-${adtRowSeq}`;
}

function blankAdtLocation() {
  return { id: newAdtRowId(), name: "", dealerNumber: "" };
}
function blankAdtLicense() {
  return { id: newAdtRowId(), description: "", licenseNumber: "" };
}
const ADT_RESIDENTIAL_PACKAGES = [
  { key: "smart-alarm-com", name: "Smart - Alarm.com", minMonthly: "$49.99", maxMonthly: "$63.99" },
  { key: "secure-alarm-com", name: "Secure - Alarm.com", minMonthly: "$42.99", maxMonthly: "$53.99" },
  { key: "complete-alarm-com", name: "Complete - Alarm.com", minMonthly: "$51.99", maxMonthly: "$69.99" },
  { key: "smart-with-touchscreen", name: "Smart With Touchscreen", minMonthly: "$49.99", maxMonthly: "$63.99" },
  { key: "secure-with-touchscreen", name: "Secure With Touchscreen", minMonthly: "$42.99", maxMonthly: "$53.99" },
  { key: "complete-with-touchscreen", name: "Complete With Touchscreen", minMonthly: "$51.99", maxMonthly: "$69.99" },
  { key: "smart-without-touchscreen", name: "Smart Without Touchscreen", minMonthly: "$49.99", maxMonthly: "$63.99" },
  { key: "secure-without-touchscreen", name: "Secure Without Touchscreen", minMonthly: "$42.99", maxMonthly: "$53.99" },
  { key: "complete-without-touchscreen", name: "Complete Without Touchscreen", minMonthly: "$51.99", maxMonthly: "$69.99" },
];

function blankAdtPackage() {
  return { id: newAdtRowId(), name: "", minMonthly: "", maxMonthly: "", adtPassthrough: "", locked: false };
}

function officialAdtPackages(savedRows) {
  const rows = Array.isArray(savedRows) ? savedRows : [];
  const byName = new Map(
    rows.map((row) => [String(row.name || "").trim().toLowerCase(), row])
  );
  const officialNames = new Set(ADT_RESIDENTIAL_PACKAGES.map((pkg) => pkg.name.toLowerCase()));
  const official = ADT_RESIDENTIAL_PACKAGES.map((pkg) => {
    const saved = byName.get(pkg.name.toLowerCase());
    return {
      id: pkg.key,
      name: pkg.name,
      minMonthly: pkg.minMonthly,
      maxMonthly: pkg.maxMonthly,
      adtPassthrough: formatAdtCurrency(saved?.adt_passthrough || saved?.adtPassthrough || ""),
      locked: true,
    };
  });
  const custom = rows
    .filter((row) => !officialNames.has(String(row.name || "").trim().toLowerCase()))
    .filter((row) => String(row.name || "").trim() || row.min_monthly || row.minMonthly || row.max_monthly || row.maxMonthly || row.adt_passthrough || row.adtPassthrough)
    .map((row) => ({
      id: newAdtRowId(),
      name: row.name || "",
      minMonthly: formatAdtCurrency(row.min_monthly || row.minMonthly || ""),
      maxMonthly: formatAdtCurrency(row.max_monthly || row.maxMonthly || ""),
      adtPassthrough: formatAdtCurrency(row.adt_passthrough || row.adtPassthrough || ""),
      locked: false,
    }));
  return custom.length ? [...official, ...custom] : official;
}
const ADT_SERVICE_ADDONS = [
  { key: "nest-aware-plus", name: "Nest Aware Plus", price: "$7.00" },
];

function blankAdtAddon() {
  return { id: newAdtRowId(), name: "", price: "", adtPassthrough: "", locked: false };
}

function officialAdtAddons(savedRows) {
  const rows = Array.isArray(savedRows) ? savedRows : [];
  const officialNames = new Set(ADT_SERVICE_ADDONS.map((addon) => addon.name.toLowerCase()));
  const official = ADT_SERVICE_ADDONS.map((addon) => {
    const saved = rows.find((row) => String(row.name || "").trim().toLowerCase() === addon.name.toLowerCase());
    return {
      id: addon.key,
      name: addon.name,
      price: addon.price,
      adtPassthrough: formatAdtCurrency(saved?.adt_passthrough || saved?.adtPassthrough || ""),
      locked: true,
    };
  });
  const custom = rows
    .filter((row) => !officialNames.has(String(row.name || "").trim().toLowerCase()))
    .filter((row) => String(row.name || "").trim() || row.price || row.adt_passthrough || row.adtPassthrough)
    .map((row) => ({
      id: newAdtRowId(),
      name: row.name || "",
      price: formatAdtCurrency(row.price),
      adtPassthrough: formatAdtCurrency(row.adt_passthrough || row.adtPassthrough || ""),
      locked: false,
    }));
  return custom.length ? [...official, ...custom] : official;
}

const ADT_FUNDING_CLASSES = ["A+", "A", "B", "B-", "D", "E", "C", "Bulk", "(Com)"];

function fundingTableFromSaved(savedRows) {
  const rows = Array.isArray(savedRows) ? savedRows : [];
  const byClass = new Map(
    rows.map((row) => [String(row.credit_class || row.creditClass || "").trim(), row])
  );
  return ADT_FUNDING_CLASSES.map((creditClass) => {
    const saved = byClass.get(creditClass) || {};
    return {
      id: creditClass,
      creditClass,
      equifaxScore: saved.equifax_score || saved.equifaxScore || "",
      fundingMultiple: saved.funding_multiple || saved.fundingMultiple || "",
      holdbackMultiple: saved.holdback_multiple || saved.holdbackMultiple || "",
    };
  });
}

function adtTermLabel(months) {
  return Number(months) === 1 ? "1 month" : `${months} months`;
}

function rowsOrBlank(rows, blank) {
  return rows.length ? rows : [blank()];
}

function parseAdtCurrency(value) {
  const raw = String(value || "").replace(/[$,\s]/g, "");
  if (!raw) return null;
  const amount = Number(raw);
  return Number.isNaN(amount) ? NaN : amount;
}

function adtSettingsSnapshot({
  enabled,
  masterDealerNumber,
  locations,
  licenses,
  packages,
  addons,
  fundingTable,
  contractTerms,
  activationFees,
  termDraft,
  feeDraft,
}) {
  const money = (value) => formatAdtCurrency(value);
  return JSON.stringify({
    enabled: !!enabled,
    masterDealerNumber: String(masterDealerNumber || "").trim(),
    locations: (locations || [])
      .filter((row) => String(row.name || "").trim() || String(row.dealerNumber || "").trim())
      .map((row) => ({
        name: String(row.name || "").trim(),
        dealerNumber: String(row.dealerNumber || "").trim(),
      })),
    licenses: (licenses || [])
      .filter((row) => String(row.description || "").trim() || String(row.licenseNumber || "").trim())
      .map((row) => ({
        description: String(row.description || "").trim(),
        licenseNumber: String(row.licenseNumber || "").trim(),
      })),
    packages: (packages || []).map((row) => ({
      name: row.name,
      minMonthly: row.minMonthly,
      maxMonthly: row.maxMonthly,
      adtPassthrough: money(row.adtPassthrough),
    })),
    addons: (addons || [])
      .filter((row) => String(row.name || "").trim() || row.price || row.adtPassthrough)
      .map((row) => ({
        name: String(row.name || "").trim(),
        price: money(row.price),
        adtPassthrough: money(row.adtPassthrough),
      })),
    fundingTable: (fundingTable || []).map((row) => ({
      creditClass: row.creditClass,
      equifaxScore: String(row.equifaxScore || "").trim(),
      fundingMultiple: formatAdtMultiple(row.fundingMultiple),
      holdbackMultiple: formatAdtMultiple(row.holdbackMultiple),
    })),
    contractTerms: [...(contractTerms || [])].map((months) => Number(months)).sort((a, b) => a - b),
    activationFees: [...(activationFees || [])]
      .map((fee) => money(fee))
      .filter(Boolean)
      .sort((a, b) => parseAdtCurrency(a) - parseAdtCurrency(b)),
    termDraft: String(termDraft || "").trim(),
    feeDraft: String(feeDraft || "").trim(),
  });
}

function formatAdtMultiple(value) {
  const amount = parseAdtCurrency(value);
  if (amount === null) return "";
  if (Number.isNaN(amount)) return String(value || "").trim();
  return amount.toFixed(2);
}

function formatAdtCurrency(value) {
  const amount = parseAdtCurrency(value);
  if (amount === null) return "";
  if (Number.isNaN(amount)) return String(value || "").trim();
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function EmailSettingsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { isReadOnly, role } = useAuth();
  const integrationReadOnly = isReadOnly || role === "Sales Director";
  const [integrationTab, setIntegrationTab] = useState("email");
  const integrationTabs = [
    { id: "email", label: "Email" },
    { id: "followup", label: "Follow-Up Intelligence" },
    { id: "sms", label: "SMS" },
    { id: "fillquick", label: "FillQuick" },
    { id: "push", label: "Web Push" },
    { id: "mapbox", label: "Mapbox" },
    { id: "webhooks", label: "Webhooks" },
    { id: "adt", label: "ADT" },
  ];
  const showIntegration = (id) => !isDesktop || integrationTab === id;
  const desktopAccordionProps = isDesktop
    ? { expanded: true, onChange: () => {} }
    : {};
  const accordionSummaryProps = isDesktop
    ? {
        expandIcon: null,
        sx: {
          cursor: "default",
          pointerEvents: "none",
          "& .MuiAccordionSummary-expandIconWrapper": { display: "none" },
        },
      }
    : { expandIcon: <ExpandMoreIcon /> };

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [followupIntelligenceAutomationEnabled, setFollowupIntelligenceAutomationEnabled] =
    useState(false);

  const [smtpSettings, setSmtpSettings] = useState({
    fromName: "",
    fromEmail: "",
    replyTo: "",
    host: "",
    port: "587",
    username: "",
    password: "",
    useTls: true,
  });

  const [defaults, setDefaults] = useState({
    leadAssigned: true,
    taskAssigned: true,
    followupCreated: true,
    followupDue: true,
    messageReceived: true,
    systemNotifications: false,
  });

  // Twilio / SMS state (API wiring later)
  const [smsEntitled] = useState(true); // TODO: from subscription/entitlements API
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsSettings, setSmsSettings] = useState({
    twilioNumber: "",
    accountSid: "",
    authToken: "",
  });

  // Web Push state (config/status - already implemented via VAPID env)
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSettings, setPushSettings] = useState({
    vapidPublicKey: "",
    vapidPrivateKey: "",
  });

  // Mapbox state (API wiring later)
  const [mapboxEnabled, setMapboxEnabled] = useState(false);
  const [mapboxSettings, setMapboxSettings] = useState({
    accessToken: "",
  });

  // Webhooks / Zapier state (API wiring later)
  const [webhooksEnabled, setWebhooksEnabled] = useState(false);
  const [webhooksSettings, setWebhooksSettings] = useState({
    webhookUrl: "",
    apiKey: "",
  });
  const [adtEnabled, setAdtEnabled] = useState(false);
  const [adtMasterDealerNumber, setAdtMasterDealerNumber] = useState("");
  const [adtLocations, setAdtLocations] = useState([blankAdtLocation()]);
  const [adtLicenses, setAdtLicenses] = useState([blankAdtLicense()]);
  const [adtPackages, setAdtPackages] = useState(() => officialAdtPackages());
  const [adtAddons, setAdtAddons] = useState(() => officialAdtAddons());
  const [adtFundingTable, setAdtFundingTable] = useState(() => fundingTableFromSaved());
  const [adtContractTerms, setAdtContractTerms] = useState([]);
  const [adtTermDraft, setAdtTermDraft] = useState("");
  const [adtActivationFees, setAdtActivationFees] = useState([]);
  const [adtFeeDraft, setAdtFeeDraft] = useState("");
  const [savingAdt, setSavingAdt] = useState(false);
  const [savedAdtSnapshot, setSavedAdtSnapshot] = useState(null);
  const [leavePrompt, setLeavePrompt] = useState(null);
  const allowLeaveRef = useRef(false);

  const [fillQuickSettings, setFillQuickSettings] = useState({
    userId: "",
    apiKey: "",
    salesRepId: "",
    leadSourceId: "1",
  });

  /** null = not loaded yet; drives StatusChip "configured" from server env (no secrets). */
  const [serverIntegrationStatus, setServerIntegrationStatus] = useState(null);

  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        const res = await api.get("/settings/email/");

        setEmailEnabled(res.data.enabled);

        setSmtpSettings({
          fromName: res.data.from_name || "",
          fromEmail: res.data.from_email || "",
          replyTo: res.data.reply_to || "",
          host: res.data.smtp_host || "",
          port: String(res.data.smtp_port || 587),
          username: res.data.smtp_username || "",
          password: "", // never preload passwords
          useTls: res.data.use_tls ?? true,
        });
      } catch (err) {
        console.error("Failed to load email settings", err);
        if (!(role === "Sales Director" && err?.response?.status === 403)) {
          toast.error("Failed to load email settings");
        }
      }
    };

    const loadProfileAndIntegrations = async () => {
      const [profOutcome, integOutcome] = await Promise.allSettled([
        api.get("/company-profile/"),
        api.get("/settings/integrations-status/"),
      ]);

      let profile = {};
      if (profOutcome.status === "fulfilled") {
        profile = profOutcome.value.data || {};
        setFollowupIntelligenceAutomationEnabled(
          !!profile.followup_intelligence_automation_enabled
        );
        setFillQuickSettings((prev) => ({
          ...prev,
          userId: profile.fillquick_user_id || "",
          apiKey: profile.fillquick_api_key || "",
          salesRepId:
            profile.fillquick_salesrep_id !== null &&
            profile.fillquick_salesrep_id !== undefined
              ? String(profile.fillquick_salesrep_id)
              : "",
          leadSourceId: String(profile.fillquick_lead_source_id || 1),
        }));
        const enabled = !!profile.adt_features_enabled;
        const dealerNumber = profile.adt_master_dealer_number || "";
        const locations = rowsOrBlank(
          (Array.isArray(profile.adt_locations) ? profile.adt_locations : []).map((row) => ({
            id: newAdtRowId(),
            name: row.name || "",
            dealerNumber: row.dealer_number || "",
          })),
          blankAdtLocation
        );
        const licenses = rowsOrBlank(
          (Array.isArray(profile.adt_licenses) ? profile.adt_licenses : []).map((row) => ({
            id: newAdtRowId(),
            description: row.description || "",
            licenseNumber: row.license_number || "",
          })),
          blankAdtLicense
        );
        const packages = officialAdtPackages(profile.adt_service_packages);
        const addons = officialAdtAddons(profile.adt_service_addons);
        const fundingTable = fundingTableFromSaved(profile.adt_funding_table);
        const terms = Array.isArray(profile.adt_contract_terms) ? profile.adt_contract_terms : [];
        const contractTerms = [...new Set(terms.map((months) => Number(months)).filter((months) => Number.isInteger(months) && months > 0))]
          .sort((a, b) => a - b);
        const fees = Array.isArray(profile.adt_activation_fees) ? profile.adt_activation_fees : [];
        const activationFees = [...new Set(fees.map((fee) => formatAdtCurrency(fee)).filter(Boolean))].sort(
          (a, b) => parseAdtCurrency(a) - parseAdtCurrency(b)
        );
        setAdtEnabled(enabled);
        setAdtMasterDealerNumber(dealerNumber);
        setAdtLocations(locations);
        setAdtLicenses(licenses);
        setAdtPackages(packages);
        setAdtAddons(addons);
        setAdtFundingTable(fundingTable);
        setAdtContractTerms(contractTerms);
        setAdtActivationFees(activationFees);
        setSavedAdtSnapshot(adtSettingsSnapshot({
          enabled,
          masterDealerNumber: dealerNumber,
          locations,
          licenses,
          packages,
          addons,
          fundingTable,
          contractTerms,
          activationFees,
          termDraft: "",
          feeDraft: "",
        }));
      } else {
        console.error("Failed to load company profile", profOutcome.reason);
      }

      let ig = {};
      if (integOutcome.status === "fulfilled") {
        ig = integOutcome.value.data || {};
        setServerIntegrationStatus({
          twilio_configured: !!ig.twilio_configured,
          mapbox_configured: !!ig.mapbox_configured,
          web_push_configured: !!ig.web_push_configured,
        });
        if (ig.twilio_phone_number) {
          setSmsSettings((prev) => ({
            ...prev,
            twilioNumber: ig.twilio_phone_number,
          }));
        }
        if (ig.vapid_public_key) {
          setPushSettings((prev) => ({
            ...prev,
            vapidPublicKey: ig.vapid_public_key,
          }));
        }
      } else {
        console.error("Failed to load integrations status", integOutcome.reason);
        setServerIntegrationStatus(null);
      }

      const twilioOk =
        integOutcome.status === "fulfilled" && !!ig.twilio_configured;
      const pushOk =
        integOutcome.status === "fulfilled" && !!ig.web_push_configured;
      const mapboxOk =
        integOutcome.status === "fulfilled" && !!ig.mapbox_configured;

      if (
        profOutcome.status === "fulfilled" ||
        integOutcome.status === "fulfilled"
      ) {
        setSmsEnabled(
          integrationToggleFromPref(
            profile.sms_integration_enabled,
            twilioOk
          )
        );
        setPushEnabled(
          integrationToggleFromPref(
            profile.web_push_integration_enabled,
            pushOk
          )
        );
        setMapboxEnabled(
          integrationToggleFromPref(
            profile.mapbox_integration_enabled,
            mapboxOk
          )
        );
      }
    };

    loadEmailSettings();
    loadProfileAndIntegrations();
  }, []);

  const handleSmtpChange = (field, value) => {
    setSmtpSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
  if (integrationReadOnly) return;
  try {
    await api.post("/settings/email/", {
      enabled: emailEnabled,
      from_name: smtpSettings.fromName,
      from_email: smtpSettings.fromEmail,
      reply_to: smtpSettings.replyTo,
      smtp_host: smtpSettings.host,
      smtp_port: Number(smtpSettings.port),
      smtp_username: smtpSettings.username,
      smtp_password: smtpSettings.password,
      use_tls: smtpSettings.useTls,
    });

    toast.success("Email settings saved");
    setSmtpSettings((prev) => ({ ...prev, password: "" }));
  } catch (err) {
    console.error("Failed to save email settings", err);
    toast.error("Failed to save email settings");
  }
};


  const handleSendTest = () => {
    // TODO: wire test email endpoint
    console.log("Send test email");
  };

  const handleFollowupIntelligenceToggle = async (checked) => {
    if (integrationReadOnly) return;
    try {
      await api.patch("/company-profile/", {
        followup_intelligence_automation_enabled: checked,
      });
      setFollowupIntelligenceAutomationEnabled(checked);
      toast.success(
        checked
          ? "Follow-up intelligence automation enabled"
          : "Follow-up intelligence automation disabled"
      );
    } catch (err) {
      console.error("Failed to save follow-up automation setting", err);
      toast.error("Failed to update follow-up automation setting");
    }
  };

  const patchIntegrationPreference = async (field, checked, label) => {
    if (integrationReadOnly) return;
    let revert = () => {};
    if (field === "sms_integration_enabled") {
      setSmsEnabled((prev) => {
        revert = () => setSmsEnabled(prev);
        return checked;
      });
    } else if (field === "web_push_integration_enabled") {
      setPushEnabled((prev) => {
        revert = () => setPushEnabled(prev);
        return checked;
      });
    } else if (field === "mapbox_integration_enabled") {
      setMapboxEnabled((prev) => {
        revert = () => setMapboxEnabled(prev);
        return checked;
      });
    }
    try {
      await api.patch("/company-profile/", { [field]: checked });
      toast.success(checked ? `${label} enabled` : `${label} disabled`);
    } catch (err) {
      revert();
      console.error("Failed to save integration preference", err);
      toast.error("Failed to update integration setting");
    }
  };

  const handleSmsSettingsChange = (field, value) => {
    setSmsSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendTestSms = () => {
    // TODO: wire test SMS endpoint
    console.log("Send test SMS");
  };

  const handleSaveSms = () => {
    // TODO: wire SMS settings API
    toast.info("SMS settings save - API not yet implemented");
  };

  const handlePushSettingsChange = (field, value) => {
    setPushSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSavePush = () =>
    toast.info("Web Push settings save - API not yet implemented");

  const handleMapboxSettingsChange = (field, value) => {
    setMapboxSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSaveMapbox = () =>
    toast.info("Mapbox settings save - API not yet implemented");

  const handleWebhooksSettingsChange = (field, value) => {
    setWebhooksSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSaveWebhooks = () =>
    toast.info("Webhooks settings save - API not yet implemented");

  const updateAdtRow = (setter, id, field, value) => {
    setter((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const handleSaveAdt = async () => {
    if (integrationReadOnly) return false;
    for (const row of adtPackages) {
      const minRaw = String(row.minMonthly || "").trim();
      const maxRaw = String(row.maxMonthly || "").trim();
      const passRaw = String(row.adtPassthrough || "").trim();
      if (!row.name.trim() && !minRaw && !maxRaw && !passRaw) continue;
      if (!row.locked && !row.name.trim()) {
        toast.error("Package name is required.");
        return false;
      }
      const minVal = parseAdtCurrency(minRaw);
      const maxVal = parseAdtCurrency(maxRaw);
      const passVal = parseAdtCurrency(passRaw);
      if (Number.isNaN(minVal)) {
        toast.error("Min monthly must be a number.");
        return false;
      }
      if (Number.isNaN(maxVal)) {
        toast.error("Max monthly must be a number.");
        return false;
      }
      if (!row.locked && (minVal === null || maxVal === null)) {
        toast.error("Custom monthly packages need a min and max monthly.");
        return false;
      }
      if (minVal !== null && maxVal !== null && maxVal < minVal) {
        toast.error("Max monthly cannot be less than min monthly.");
        return false;
      }
      if (Number.isNaN(passVal)) {
        toast.error("ADT Passthrough must be a number.");
        return false;
      }
    }
    for (const row of adtAddons) {
      const priceRaw = String(row.price || "").trim();
      const passRaw = String(row.adtPassthrough || "").trim();
      if (!row.name.trim() && !priceRaw && !passRaw) continue;
      if (Number.isNaN(parseAdtCurrency(priceRaw))) {
        toast.error("Add-on price must be a number.");
        return false;
      }
      if (Number.isNaN(parseAdtCurrency(passRaw))) {
        toast.error("ADT Passthrough must be a number.");
        return false;
      }
    }
    for (const row of adtFundingTable) {
      const funding = parseAdtCurrency(row.fundingMultiple);
      const holdback = parseAdtCurrency(row.holdbackMultiple);
      if (Number.isNaN(funding)) {
        toast.error(`Funding multiple for ${row.creditClass} must be a number.`);
        return false;
      }
      if (Number.isNaN(holdback)) {
        toast.error(`Holdback multiple for ${row.creditClass} must be a number.`);
        return false;
      }
      if ((funding !== null && funding < 0) || (holdback !== null && holdback < 0)) {
        toast.error(`Multiples for ${row.creditClass} cannot be negative.`);
        return false;
      }
    }
    const formattedPackages = adtPackages.map((row) => ({
      ...row,
      minMonthly: formatAdtCurrency(row.minMonthly),
      maxMonthly: formatAdtCurrency(row.maxMonthly),
      adtPassthrough: formatAdtCurrency(row.adtPassthrough),
    }));
    setAdtPackages(formattedPackages);
    const formattedAddons = adtAddons.map((row) => ({
      ...row,
      price: formatAdtCurrency(row.price),
      adtPassthrough: formatAdtCurrency(row.adtPassthrough),
    }));
    setAdtAddons(formattedAddons);
    const formattedFunding = adtFundingTable.map((row) => ({
      ...row,
      equifaxScore: String(row.equifaxScore || "").trim(),
      fundingMultiple: formatAdtMultiple(row.fundingMultiple),
      holdbackMultiple: formatAdtMultiple(row.holdbackMultiple),
    }));
    setAdtFundingTable(formattedFunding);
    setSavingAdt(true);
    try {
      await api.patch("/company-profile/", {
        adt_features_enabled: adtEnabled,
        adt_master_dealer_number: adtMasterDealerNumber.trim(),
        adt_locations: adtLocations
          .filter((row) => row.name.trim() || row.dealerNumber.trim())
          .map((row) => ({
            name: row.name.trim(),
            dealer_number: row.dealerNumber.trim(),
          })),
        adt_licenses: adtLicenses
          .filter((row) => row.description.trim() || row.licenseNumber.trim())
          .map((row) => ({
            description: row.description.trim(),
            license_number: row.licenseNumber.trim(),
          })),
        adt_service_packages: formattedPackages.map((row) => ({
          name: row.name,
          min_monthly: row.minMonthly,
          max_monthly: row.maxMonthly,
          adt_passthrough: row.adtPassthrough,
        })),
        adt_service_addons: formattedAddons
          .filter((row) => row.name.trim() || row.price || row.adtPassthrough)
          .map((row) => ({
            name: row.name.trim(),
            price: row.price,
            adt_passthrough: row.adtPassthrough,
          })),
        adt_funding_table: formattedFunding.map((row) => ({
          credit_class: row.creditClass,
          equifax_score: row.equifaxScore,
          funding_multiple: row.fundingMultiple,
          holdback_multiple: row.holdbackMultiple,
        })),
        adt_contract_terms: [...adtContractTerms].sort((a, b) => a - b),
        adt_activation_fees: [...adtActivationFees].sort(
          (a, b) => parseAdtCurrency(a) - parseAdtCurrency(b)
        ),
      });
      setSavedAdtSnapshot(adtSettingsSnapshot({
        enabled: adtEnabled,
        masterDealerNumber: adtMasterDealerNumber,
        locations: adtLocations,
        licenses: adtLicenses,
        packages: formattedPackages,
        addons: formattedAddons,
        fundingTable: formattedFunding,
        contractTerms: adtContractTerms,
        activationFees: adtActivationFees,
        termDraft: adtTermDraft,
        feeDraft: adtFeeDraft,
      }));
      toast.success("ADT settings saved");
      return true;
    } catch (err) {
      console.error("Failed to save ADT settings", err);
      const detail = err?.response?.data;
      const message =
        detail?.adt_service_packages?.[0] ||
        detail?.adt_service_addons?.[0] ||
        detail?.adt_funding_table?.[0] ||
        detail?.adt_activation_fees?.[0] ||
        detail?.detail ||
        "Failed to save ADT settings";
      toast.error(typeof message === "string" ? message : "Failed to save ADT settings");
      return false;
    } finally {
      setSavingAdt(false);
    }
  };
  const handleAddAdtTerm = () => {
    if (integrationReadOnly) return;
    const months = Number(String(adtTermDraft).trim());
    if (!Number.isInteger(months) || months < 1) {
      toast.error("Enter a whole number of months.");
      return;
    }
    if (adtContractTerms.includes(months)) {
      toast.error("That contract term is already added.");
      return;
    }
    setAdtContractTerms((prev) => [...prev, months].sort((a, b) => a - b));
    setAdtTermDraft("");
  };

  const handleAddAdtFee = () => {
    if (integrationReadOnly) return;
    const amount = parseAdtCurrency(adtFeeDraft);
    if (amount === null || Number.isNaN(amount) || amount <= 0) {
      toast.error("Enter an activation fee.");
      return;
    }
    const formatted = formatAdtCurrency(adtFeeDraft);
    if (adtActivationFees.some((fee) => Math.round(parseAdtCurrency(fee) * 100) === Math.round(amount * 100))) {
      toast.error("That activation fee is already added.");
      return;
    }
    setAdtActivationFees((prev) =>
      [...prev, formatted].sort((a, b) => parseAdtCurrency(a) - parseAdtCurrency(b))
    );
    setAdtFeeDraft("");
  };

  const handleFillQuickSettingsChange = (field, value) => {
    setFillQuickSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSaveFillQuick = async () => {
    if (integrationReadOnly) return;
    try {
      await api.patch("/company-profile/", {
        fillquick_user_id: fillQuickSettings.userId || "",
        fillquick_api_key: fillQuickSettings.apiKey || "",
        fillquick_salesrep_id: fillQuickSettings.salesRepId
          ? Number(fillQuickSettings.salesRepId)
          : null,
        fillquick_lead_source_id: Number(fillQuickSettings.leadSourceId || 1),
      });
      toast.success("FillQuick settings saved");
    } catch (err) {
      console.error("Failed to save FillQuick settings", err);
      toast.error("Failed to save FillQuick settings");
    }
  };

  const disabledSection = integrationReadOnly || !emailEnabled;
  const disabledSmsSection = integrationReadOnly || !smsEnabled;
  const disabledPushSection = integrationReadOnly || !pushEnabled;
  const disabledMapboxSection = integrationReadOnly || !mapboxEnabled;
  const disabledWebhooksSection = integrationReadOnly || !webhooksEnabled;
  const smsConfigured =
    serverIntegrationStatus !== null
      ? serverIntegrationStatus.twilio_configured
      : Boolean(
          smsSettings.twilioNumber?.trim() &&
            smsSettings.accountSid?.trim() &&
            smsSettings.authToken?.trim()
        );
  const pushConfigured =
    serverIntegrationStatus !== null
      ? serverIntegrationStatus.web_push_configured
      : Boolean(
          pushSettings.vapidPublicKey?.trim() &&
            pushSettings.vapidPrivateKey?.trim()
        );
  const mapboxConfigured =
    serverIntegrationStatus !== null
      ? serverIntegrationStatus.mapbox_configured
      : Boolean(mapboxSettings.accessToken?.trim());
  const webhooksConfigured = Boolean(webhooksSettings.webhookUrl?.trim());
  const fillQuickConfigured = Boolean(
    fillQuickSettings.userId?.trim() && fillQuickSettings.apiKey?.trim()
  );
  const currentAdtSnapshot = adtSettingsSnapshot({
    enabled: adtEnabled,
    masterDealerNumber: adtMasterDealerNumber,
    locations: adtLocations,
    licenses: adtLicenses,
    packages: adtPackages,
    addons: adtAddons,
    fundingTable: adtFundingTable,
    contractTerms: adtContractTerms,
    activationFees: adtActivationFees,
    termDraft: adtTermDraft,
    feeDraft: adtFeeDraft,
  });
  const adtDirty = savedAdtSnapshot != null && currentAdtSnapshot !== savedAdtSnapshot;

  useEffect(() => {
    if (!adtDirty) return undefined;
    const onBeforeUnload = (event) => {
      if (allowLeaveRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [adtDirty]);

  useEffect(() => {
    if (!adtDirty) return undefined;
    window.history.pushState({ adtUnsavedGuard: true }, "");
    const onPop = () => {
      if (allowLeaveRef.current) return;
      setLeavePrompt({ back: true });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [adtDirty]);

  useEffect(() => {
    if (!adtDirty) return undefined;
    const onClick = (event) => {
      if (allowLeaveRef.current) return;
      if (leavePrompt) {
        const openAnchor = event.target.closest?.("a[href]");
        if (openAnchor) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const rawHref = anchor.getAttribute("href") || "";
      if (!rawHref || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) return;
      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const next = `${url.pathname}${url.search}${url.hash}`;
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (next === here) return;
      event.preventDefault();
      event.stopPropagation();
      setLeavePrompt({ href: next });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [adtDirty, leavePrompt]);

  const dismissLeavePrompt = () => {
    setLeavePrompt((current) => {
      if (current?.back) window.history.pushState({ adtUnsavedGuard: true }, "");
      return null;
    });
  };

  const leaveWithoutSaving = () => {
    const dest = leavePrompt;
    allowLeaveRef.current = true;
    setLeavePrompt(null);
    if (dest?.back) navigate(-1);
    else if (dest?.href) navigate(dest.href, { replace: true });
  };

  const saveAndLeave = async () => {
    const saved = await handleSaveAdt();
    if (!saved) return;
    leaveWithoutSaving();
  };

  return (
    <Box sx={{ p: 4, px: { xs: 1, sm: 4 } }}>
<Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
      {isDesktop && (
        <Box
          data-tour="integrations-services"
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.25,
            p: 0.5,
            mb: 3,
            width: "fit-content",
            maxWidth: "100%",
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#fff",
            borderRadius: "999px",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
          }}
        >
          {integrationTabs.map((tab) => {
            const selected = integrationTab === tab.id;
            return (
              <Button
                key={tab.id}
                disableRipple
                onClick={() => setIntegrationTab(tab.id)}
                sx={{
                  textTransform: "none",
                  borderRadius: "999px",
                  px: 2,
                  py: 0.75,
                  minWidth: 0,
                  fontWeight: selected ? 700 : 500,
                  fontSize: "0.95rem",
                  color: selected ? "#fff" : "text.secondary",
                  bgcolor: selected ? "primary.main" : "transparent",
                  "&:hover": {
                    bgcolor: selected ? "primary.dark" : "action.hover",
                  },
                }}
              >
                {tab.label}
              </Button>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: showIntegration("email") ? "block" : "none" }}>
      {/* ---------------- Header Card ---------------- */}
      <Card sx={{ p: 3, mb: 5, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(25,118,210,0.08)",
              }}
            >
              <EmailIcon color="primary" />
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                Email Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System-wide email configuration (Admin only)
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={handleSendTest}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Send Test Email
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Stack>
        </Stack>
        {role === "Sales Director" && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            View-only access: only Admin can modify integrations settings.
          </Typography>
        )}
      </Card>

      {/* ---------------- Main Grid ---------------- */}
      <Box
        data-tour="integrations-email"
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
        gap={4}
        sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
      >
        {/* ---------------- Card 1: Global Toggle ---------------- */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Email Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable or disable all outgoing email from the CRM.
            </Typography>

            <SettingRow
              label="Enable Email Notifications"
              checked={emailEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                setEmailEnabled(checked);
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        {/* ---------------- Card 2: SMTP Provider ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Email Provider (SMTP)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure how emails are sent from the system.
            </Typography>

            <Stack spacing={2}>
              <TextField
                size="small"
                label="From Name"
                value={smtpSettings.fromName}
                onChange={(e) => handleSmtpChange("fromName", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="From Email"
                value={smtpSettings.fromEmail}
                onChange={(e) => handleSmtpChange("fromEmail", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="Reply-To Email"
                value={smtpSettings.replyTo}
                onChange={(e) => handleSmtpChange("replyTo", e.target.value)}
                disabled={disabledSection}
              />

              <Box sx={{ height: 8 }} />

              <TextField
                size="small"
                label="SMTP Host"
                value={smtpSettings.host}
                onChange={(e) => handleSmtpChange("host", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="SMTP Port"
                value={smtpSettings.port}
                onChange={(e) => handleSmtpChange("port", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="SMTP Username"
                value={smtpSettings.username}
                onChange={(e) => handleSmtpChange("username", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                type="password"
                label="SMTP Password"
                value={smtpSettings.password}
                onChange={(e) => handleSmtpChange("password", e.target.value)}
                disabled={disabledSection}
              />

              <SettingRow
                label="Use TLS"
                checked={smtpSettings.useTls}
                onChange={(val) => handleSmtpChange("useTls", val)}
                disabled={disabledSection}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ---------------- Card 3: Default Preferences ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Default Email Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Applied to new users. Individuals can override later.
            </Typography>

            <Stack spacing={1}>
              <SettingRow
                label="Lead assigned"
                description="When a lead is assigned to a user"
                checked={defaults.leadAssigned}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, leadAssigned: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="Task assigned"
                description="When a task is assigned"
                checked={defaults.taskAssigned}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, taskAssigned: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="Follow-up created"
                description="When a follow-up is created"
                checked={defaults.followupCreated}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, followupCreated: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="Follow-up due"
                description="Reminder on the day a follow-up is due"
                checked={defaults.followupDue}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, followupDue: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="New internal message"
                description="When a message is received"
                checked={defaults.messageReceived}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, messageReceived: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="System notifications"
                description="Low priority system messages"
                checked={defaults.systemNotifications}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, systemNotifications: val }))
                }
                disabled={disabledSection}
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>
      </Box>

      {/* ---------------- Follow-Up Intelligence (automation) ---------------- */}
      <Accordion
        defaultExpanded
        {...desktopAccordionProps}
        sx={{
          mb: 4,
          borderRadius: 3,
          mt: { xs: 6, md: 0 },
          overflow: "hidden",
          display: showIntegration("followup") ? undefined : "none",
        }}
      >
        <AccordionSummary {...accordionSummaryProps}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <AutoAwesomeIcon color="primary" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Follow-Up Intelligence
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Turn on automatic follow-up messages. Templates and channel switches are on the follow-up settings page.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>
          <SettingRow
            label="Enable follow-up automation"
            description="When this is on, the scheduled job can send the customer emails and texts you set up."
            checked={followupIntelligenceAutomationEnabled}
            onChange={handleFollowupIntelligenceToggle}
            disabled={integrationReadOnly}
          />
          {followupIntelligenceAutomationEnabled && (
            <Button
              variant="outlined"
              onClick={() => navigate("/follow-up-intelligence/settings")}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, mt: 1 }}
            >
              Open follow-up settings
            </Button>
          )}
        </AccordionDetails>
      </Accordion>

      <Box {...(!isDesktop ? { "data-tour": "integrations-services" } : {})}>
      {/* ---------------- Twilio / SMS Section ---------------- */}
      <Accordion
        {...desktopAccordionProps}
        sx={{
          mb: 4,
          borderRadius: 3,
          mt: { xs: 6, md: 0 },
          overflow: "hidden",
          display: showIntegration("sms") ? undefined : "none",
        }}
      >
        <AccordionSummary {...accordionSummaryProps}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <SmsIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    SMS (Twilio)
                  </Typography>
                  <StatusChip enabled={smsEnabled} configured={smsConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Allows Two-way SMS conversations with leads within the CRM. Subscribe or add your existing Twilio Credentials
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        {/* ---------------- Card 1: SMS Global Toggle ---------------- */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              SMS Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable or disable SMS conversations with leads.
            </Typography>
            <SettingRow
              label="Enable SMS Conversations"
              checked={smsEnabled}
              onChange={(checked) => {
                if (integrationReadOnly || !smsEntitled) return;
                patchIntegrationPreference(
                  "sms_integration_enabled",
                  checked,
                  "SMS conversations"
                );
              }}
              disabled={integrationReadOnly || !smsEntitled}
            />
          </CardContent>
        </Card>

        {/* ---------------- Card 2: Twilio Provider ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSmsSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Twilio Provider
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure Twilio phone number for SMS.
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Twilio Phone Number"
                placeholder="+1234567890"
                value={smsSettings.twilioNumber}
                onChange={(e) =>
                  handleSmsSettingsChange("twilioNumber", e.target.value)
                }
                disabled={disabledSmsSection}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ---------------- Card 3: Twilio Credentials ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSmsSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Twilio Credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Account SID and Auth Token from your Twilio console.
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Account SID"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={smsSettings.accountSid}
                onChange={(e) =>
                  handleSmsSettingsChange("accountSid", e.target.value)
                }
                disabled={disabledSmsSection}
                fullWidth
              />
              <TextField
                size="small"
                type="password"
                label="Auth Token"
                placeholder="Your secret auth token"
                value={smsSettings.authToken}
                onChange={(e) =>
                  handleSmsSettingsChange("authToken", e.target.value)
                }
                disabled={disabledSmsSection}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={handleSendTestSms}
                sx={{ textTransform: "none" }}
                disabled={!smsEnabled || integrationReadOnly}
              >
                Send Test SMS
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveSms}
                sx={{ textTransform: "none" }}
                disabled={integrationReadOnly}
              >
                Save SMS Settings
              </Button>
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- FillQuick CRM Integration ---------------- */}
      <Accordion
        {...desktopAccordionProps}
        sx={{
          mb: 4,
          borderRadius: 3,
          mt: { xs: 6, md: 0 },
          overflow: "hidden",
          display: showIntegration("fillquick") ? undefined : "none",
        }}
      >
        <AccordionSummary {...accordionSummaryProps}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <IntegrationInstructionsIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    FillQuick CRM
                  </Typography>
                  <StatusChip enabled={fillQuickConfigured} configured={fillQuickConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Admin setup for FillQuick lead creation (push Closed Won leads from Territory CRM).
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pt: 0 }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={700} mb={1}>
                FillQuick API Credentials (Admin)
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter the FillQuick User ID and API key provided by FillQuick admin setup.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  size="small"
                  label="FillQuick User ID"
                  value={fillQuickSettings.userId}
                  onChange={(e) => handleFillQuickSettingsChange("userId", e.target.value)}
                  disabled={integrationReadOnly}
                  fullWidth
                />
                <TextField
                  size="small"
                  type="password"
                  label="FillQuick API Key"
                  value={fillQuickSettings.apiKey}
                  onChange={(e) => handleFillQuickSettingsChange("apiKey", e.target.value)}
                  disabled={integrationReadOnly}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Default FillQuick Sales Rep ID (optional)"
                  value={fillQuickSettings.salesRepId}
                  onChange={(e) => handleFillQuickSettingsChange("salesRepId", e.target.value)}
                  disabled={integrationReadOnly}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Lead Source ID (self-generated)"
                  value={fillQuickSettings.leadSourceId}
                  disabled
                  fullWidth
                  helperText="Default FillQuick lead source for self-generated leads is 1."
                />
              </Stack>
            </CardContent>
          </Card>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveFillQuick}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save FillQuick Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- Web Push Section ---------------- */}
      <Accordion
        {...desktopAccordionProps}
        sx={{
          mb: 4,
          borderRadius: 3,
          mt: { xs: 6, md: 0 },
          overflow: "hidden",
          display: showIntegration("push") ? undefined : "none",
        }}
      >
        <AccordionSummary {...accordionSummaryProps}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <NotificationsActiveIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Web Push
                  </Typography>
                  <StatusChip enabled={pushEnabled} configured={pushConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Enables Team Chat @mentions and browser notifications from the CRM. Included feature. Request credentials
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Web Push Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable browser push for Team Chat @mentions.
            </Typography>
            <SettingRow
              label="Enable Web Push"
              checked={pushEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                patchIntegrationPreference(
                  "web_push_integration_enabled",
                  checked,
                  "Web push"
                );
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledPushSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              VAPID Public Key
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Used by frontend to subscribe. Set REACT_APP_VAPID_PUBLIC_KEY in env.
            </Typography>
            <TextField
              size="small"
              label="VAPID Public Key"
              placeholder="BKxxxxxxxx..."
              value={pushSettings.vapidPublicKey}
              onChange={(e) =>
                handlePushSettingsChange("vapidPublicKey", e.target.value)
              }
              disabled={disabledPushSection}
              fullWidth
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledPushSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              VAPID Private Key
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Backend only. Set VAPID_PRIVATE_KEY in env. Never expose.
            </Typography>
            <TextField
              size="small"
              type="password"
              label="VAPID Private Key"
              placeholder="••••••••"
              value={pushSettings.vapidPrivateKey}
              onChange={(e) =>
                handlePushSettingsChange("vapidPrivateKey", e.target.value)
              }
              disabled={disabledPushSection}
              fullWidth
            />
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={handleSavePush}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- Mapbox Geocoding Section ---------------- */}
      <Accordion
        {...desktopAccordionProps}
        sx={{
          mb: 4,
          borderRadius: 3,
          mt: { xs: 6, md: 0 },
          overflow: "hidden",
          display: showIntegration("mapbox") ? undefined : "none",
        }}
      >
        <AccordionSummary {...accordionSummaryProps}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <MapIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Mapbox Geocoding
                  </Typography>
                  <StatusChip enabled={mapboxEnabled} configured={mapboxConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Enables Address geocoding for leads and territories. For high volume geocoding visit: https://www.mapbox.com/, for your API Token
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Geocoding
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable Mapbox for address → lat/lon. Falls back to geocode.maps.co and Nominatim if disabled.
            </Typography>
            <SettingRow
              label="Enable Mapbox Geocoding"
              checked={mapboxEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                patchIntegrationPreference(
                  "mapbox_integration_enabled",
                  checked,
                  "Mapbox geocoding"
                );
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledMapboxSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Mapbox Access Token
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              From Mapbox account. Set GEOCODE_MAPBOX_ACCESS_TOKEN or MAPBOX_ACCESS_TOKEN in env.
            </Typography>
            <TextField
              size="small"
              type="password"
              label="Access Token"
              placeholder="pk.xxxxxxxx..."
              value={mapboxSettings.accessToken}
              onChange={(e) =>
                handleMapboxSettingsChange("accessToken", e.target.value)
              }
              disabled={disabledMapboxSection}
              fullWidth
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledMapboxSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Info
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mapbox is used for lead import, territory mapping, and address geocoding. When disabled, the system falls back to free services (geocode.maps.co, Nominatim).
            </Typography>
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveMapbox}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- Webhooks / Zapier Section ---------------- */}
      <Accordion
        {...desktopAccordionProps}
        sx={{
          mb: 4,
          borderRadius: 3,
          mt: { xs: 6, md: 0 },
          overflow: "hidden",
          display: showIntegration("webhooks") ? undefined : "none",
        }}
      >
        <AccordionSummary {...accordionSummaryProps}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <IntegrationInstructionsIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Webhooks / Zapier
                  </Typography>
                  <StatusChip enabled={webhooksEnabled} configured={webhooksConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Send events to external tools (Zapier, Make, etc.)
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Webhooks
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable outbound webhooks for lead created, conversation updated, etc.
            </Typography>
            <SettingRow
              label="Enable Webhooks"
              checked={webhooksEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                setWebhooksEnabled(checked);
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledWebhooksSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Webhook URL
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Zapier/Make webhook URL or your endpoint.
            </Typography>
            <TextField
              size="small"
              label="Webhook URL"
              placeholder="https://hooks.zapier.com/..."
              value={webhooksSettings.webhookUrl}
              onChange={(e) =>
                handleWebhooksSettingsChange("webhookUrl", e.target.value)
              }
              disabled={disabledWebhooksSection}
              fullWidth
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledWebhooksSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              API Key (optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Sent as X-API-Key header for webhook auth.
            </Typography>
            <TextField
              size="small"
              type="password"
              label="API Key"
              placeholder="Optional"
              value={webhooksSettings.apiKey}
              onChange={(e) =>
                handleWebhooksSettingsChange("apiKey", e.target.value)
              }
              disabled={disabledWebhooksSection}
              fullWidth
            />
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveWebhooks}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        {...desktopAccordionProps}
        sx={{
          mb: 4,
          borderRadius: 3,
          mt: { xs: 6, md: 0 },
          overflow: "hidden",
          display: showIntegration("adt") ? undefined : "none",
        }}
      >
        <AccordionSummary {...accordionSummaryProps}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 44, sm: 56 },
                  height: { xs: 44, sm: 56 },
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <ShieldIcon color="primary" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" fontWeight={700}>
                    ADT
                  </Typography>
                  <StatusChip
                    enabled={adtEnabled}
                    configured={Boolean(adtMasterDealerNumber.trim())}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                  Dealer numbers, locations, licenses, and monthly service.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 3 }}>
          <Stack spacing={3} divider={<SectionRule />}>
            <Box>
              <SettingRow
                label="Enable ADT Features"
                description="Turn on ADT dealer tools for this organization."
                checked={adtEnabled}
                onChange={(checked) => {
                  if (integrationReadOnly) return;
                  setAdtEnabled(checked);
                }}
                disabled={integrationReadOnly}
              />
              <TextField
                size="small"
                label="Master Dealer #"
                value={adtMasterDealerNumber}
                onChange={(e) => setAdtMasterDealerNumber(e.target.value)}
                disabled={integrationReadOnly}
                sx={{ mt: 1, width: { xs: "100%", sm: "67%" }, maxWidth: { sm: 241 } }}
              />
            </Box>

            <Box>
              <Typography fontWeight={700} mb={1.5}>Locations</Typography>
              <Stack spacing={1.5}>
                {adtLocations.map((row) => (
                  <Stack key={row.id} direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                    <TextField
                      size="small"
                      label="Location Name"
                      value={row.name}
                      onChange={(e) => updateAdtRow(setAdtLocations, row.id, "name", e.target.value)}
                      disabled={integrationReadOnly}
                      sx={{ width: { xs: "100%", sm: "33.5%" }, flex: { sm: "0 0 33.5%" } }}
                    />
                    <TextField
                      size="small"
                      label="Dealer #"
                      value={row.dealerNumber}
                      onChange={(e) => updateAdtRow(setAdtLocations, row.id, "dealerNumber", e.target.value)}
                      disabled={integrationReadOnly}
                      sx={{ width: { xs: "100%", sm: "22.5%" }, flex: { sm: "0 0 22.5%" } }}
                    />
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() =>
                        setAdtLocations((prev) =>
                          prev.length === 1 ? [blankAdtLocation()] : prev.filter((item) => item.id !== row.id)
                        )
                      }
                      disabled={integrationReadOnly}
                      sx={{ textTransform: "none", flexShrink: 0 }}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small"
                onClick={() => setAdtLocations((prev) => [...prev, blankAdtLocation()])}
                disabled={integrationReadOnly}
                sx={{ textTransform: "none", mt: 1.5 }}
              >
                + Add Location
              </Button>
            </Box>

            <Box>
              <Typography fontWeight={700} mb={1.5}>License Numbers</Typography>
              <Stack spacing={1.5}>
                {adtLicenses.map((row) => (
                  <Stack key={row.id} direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                    <TextField
                      size="small"
                      label="Description"
                      value={row.description}
                      onChange={(e) => updateAdtRow(setAdtLicenses, row.id, "description", e.target.value)}
                      disabled={integrationReadOnly}
                      sx={{ width: { xs: "100%", sm: "33.5%" }, flex: { sm: "0 0 33.5%" } }}
                    />
                    <TextField
                      size="small"
                      label="License #"
                      value={row.licenseNumber}
                      onChange={(e) => updateAdtRow(setAdtLicenses, row.id, "licenseNumber", e.target.value)}
                      disabled={integrationReadOnly}
                      sx={{ width: { xs: "100%", sm: "22.5%" }, flex: { sm: "0 0 22.5%" } }}
                    />
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() =>
                        setAdtLicenses((prev) =>
                          prev.length === 1 ? [blankAdtLicense()] : prev.filter((item) => item.id !== row.id)
                        )
                      }
                      disabled={integrationReadOnly}
                      sx={{ textTransform: "none", flexShrink: 0 }}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small"
                onClick={() => setAdtLicenses((prev) => [...prev, blankAdtLicense()])}
                disabled={integrationReadOnly}
                sx={{ textTransform: "none", mt: 1.5 }}
              >
                + Add License
              </Button>
            </Box>

            <Box>
              <Typography fontWeight={700} mb={0.5}>ADT Funding Table</Typography>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Credit class is fixed. Enter the Equifax score, funding multiple, and holdback multiple for each class.
              </Typography>
              <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
                {adtFundingTable.map((row) => (
                  <Box
                    key={row.id}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}
                  >
                    <Typography fontWeight={700} sx={{ mb: 1.25 }}>{row.creditClass}</Typography>
                    <Stack spacing={1.25}>
                      <TextField
                        size="small"
                        label="Equifax Score"
                        value={row.equifaxScore}
                        onChange={(e) => updateAdtRow(setAdtFundingTable, row.id, "equifaxScore", e.target.value)}
                        disabled={integrationReadOnly}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Funding Multiple"
                        placeholder="0.00"
                        value={row.fundingMultiple}
                        onChange={(e) => updateAdtRow(setAdtFundingTable, row.id, "fundingMultiple", e.target.value)}
                        onBlur={() => updateAdtRow(setAdtFundingTable, row.id, "fundingMultiple", formatAdtMultiple(row.fundingMultiple))}
                        disabled={integrationReadOnly}
                        inputProps={{ inputMode: "decimal" }}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Holdback Multiple"
                        placeholder="0.00"
                        value={row.holdbackMultiple}
                        onChange={(e) => updateAdtRow(setAdtFundingTable, row.id, "holdbackMultiple", e.target.value)}
                        onBlur={() => updateAdtRow(setAdtFundingTable, row.id, "holdbackMultiple", formatAdtMultiple(row.holdbackMultiple))}
                        disabled={integrationReadOnly}
                        inputProps={{ inputMode: "decimal" }}
                        fullWidth
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
              <Table size="small" sx={{ width: "78%", tableLayout: "fixed", display: { xs: "none", md: "table" } }}>
                <colgroup>
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "28%" }} />
                  <col style={{ width: "27%" }} />
                  <col style={{ width: "27%" }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell>Credit Class</TableCell>
                    <TableCell>Equifax Score</TableCell>
                    <TableCell>Funding Multiple</TableCell>
                    <TableCell>Holdback Multiple</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adtFundingTable.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ pl: 0, fontWeight: 700 }}>{row.creditClass}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Score"
                          value={row.equifaxScore}
                          onChange={(e) => updateAdtRow(setAdtFundingTable, row.id, "equifaxScore", e.target.value)}
                          disabled={integrationReadOnly}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="0.00"
                          value={row.fundingMultiple}
                          onChange={(e) => updateAdtRow(setAdtFundingTable, row.id, "fundingMultiple", e.target.value)}
                          onBlur={() => updateAdtRow(setAdtFundingTable, row.id, "fundingMultiple", formatAdtMultiple(row.fundingMultiple))}
                          disabled={integrationReadOnly}
                          inputProps={{ inputMode: "decimal" }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ pr: 0 }}>
                        <TextField
                          size="small"
                          placeholder="0.00"
                          value={row.holdbackMultiple}
                          onChange={(e) => updateAdtRow(setAdtFundingTable, row.id, "holdbackMultiple", e.target.value)}
                          onBlur={() => updateAdtRow(setAdtFundingTable, row.id, "holdbackMultiple", formatAdtMultiple(row.holdbackMultiple))}
                          disabled={integrationReadOnly}
                          inputProps={{ inputMode: "decimal" }}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography fontWeight={700} mb={0.5}>Monthly Service</Typography>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Residential package names and price ranges are set by ADT. Add a custom package when you need one.
              </Typography>
              <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
                {adtPackages.map((row) => (
                  <Box
                    key={row.id}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}
                  >
                    {row.locked ? (
                      <Typography fontWeight={700} sx={{ overflowWrap: "anywhere", mb: 1 }}>{row.name}</Typography>
                    ) : (
                      <TextField
                        size="small"
                        label="Package name"
                        value={row.name}
                        onChange={(e) => updateAdtRow(setAdtPackages, row.id, "name", e.target.value)}
                        disabled={integrationReadOnly}
                        fullWidth
                        sx={{ mb: 1.25 }}
                      />
                    )}
                    <Stack direction="row" spacing={1}>
                      {row.locked ? (
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          Min {row.minMonthly}
                        </Typography>
                      ) : (
                        <TextField
                          size="small"
                          label="Min. monthly"
                          placeholder="$0.00"
                          value={row.minMonthly}
                          onChange={(e) => updateAdtRow(setAdtPackages, row.id, "minMonthly", e.target.value)}
                          onBlur={() => updateAdtRow(setAdtPackages, row.id, "minMonthly", formatAdtCurrency(row.minMonthly))}
                          disabled={integrationReadOnly}
                          inputProps={{ inputMode: "decimal" }}
                          fullWidth
                        />
                      )}
                      {row.locked ? (
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          Max {row.maxMonthly}
                        </Typography>
                      ) : (
                        <TextField
                          size="small"
                          label="Max. monthly"
                          placeholder="$0.00"
                          value={row.maxMonthly}
                          onChange={(e) => updateAdtRow(setAdtPackages, row.id, "maxMonthly", e.target.value)}
                          onBlur={() => updateAdtRow(setAdtPackages, row.id, "maxMonthly", formatAdtCurrency(row.maxMonthly))}
                          disabled={integrationReadOnly}
                          inputProps={{ inputMode: "decimal" }}
                          fullWidth
                        />
                      )}
                    </Stack>
                    <TextField
                      size="small"
                      label="ADT Passthrough"
                      placeholder="$0.00"
                      value={row.adtPassthrough}
                      onChange={(e) => updateAdtRow(setAdtPackages, row.id, "adtPassthrough", e.target.value)}
                      onBlur={() => updateAdtRow(setAdtPackages, row.id, "adtPassthrough", formatAdtCurrency(row.adtPassthrough))}
                      disabled={integrationReadOnly}
                      inputProps={{ inputMode: "decimal" }}
                      fullWidth
                      sx={{ mt: 1.25 }}
                    />
                    {!row.locked && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => setAdtPackages((prev) => prev.filter((item) => item.id !== row.id))}
                        disabled={integrationReadOnly}
                        sx={{ textTransform: "none", mt: 1 }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))}
              </Stack>
              <Table size="small" sx={{ width: "78%", tableLayout: "fixed", display: { xs: "none", md: "table" } }}>
                <colgroup>
                  <col style={{ width: "34%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell>Package Name</TableCell>
                    <TableCell>Min. Monthly</TableCell>
                    <TableCell>Max. Monthly</TableCell>
                    <TableCell>ADT Passthrough</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adtPackages.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ pl: 0 }}>
                        {row.locked ? row.name : (
                          <TextField
                            size="small"
                            placeholder="Package name"
                            value={row.name}
                            onChange={(e) => updateAdtRow(setAdtPackages, row.id, "name", e.target.value)}
                            disabled={integrationReadOnly}
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.locked ? row.minMonthly : (
                          <TextField
                            size="small"
                            placeholder="$0.00"
                            value={row.minMonthly}
                            onChange={(e) => updateAdtRow(setAdtPackages, row.id, "minMonthly", e.target.value)}
                            onBlur={() => updateAdtRow(setAdtPackages, row.id, "minMonthly", formatAdtCurrency(row.minMonthly))}
                            disabled={integrationReadOnly}
                            inputProps={{ inputMode: "decimal" }}
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.locked ? row.maxMonthly : (
                          <TextField
                            size="small"
                            placeholder="$0.00"
                            value={row.maxMonthly}
                            onChange={(e) => updateAdtRow(setAdtPackages, row.id, "maxMonthly", e.target.value)}
                            onBlur={() => updateAdtRow(setAdtPackages, row.id, "maxMonthly", formatAdtCurrency(row.maxMonthly))}
                            disabled={integrationReadOnly}
                            inputProps={{ inputMode: "decimal" }}
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="$0.00"
                          value={row.adtPassthrough}
                          onChange={(e) => updateAdtRow(setAdtPackages, row.id, "adtPassthrough", e.target.value)}
                          onBlur={() => updateAdtRow(setAdtPackages, row.id, "adtPassthrough", formatAdtCurrency(row.adtPassthrough))}
                          disabled={integrationReadOnly}
                          inputProps={{ inputMode: "decimal" }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right">
                        {!row.locked && (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => setAdtPackages((prev) => prev.filter((item) => item.id !== row.id))}
                            disabled={integrationReadOnly}
                            sx={{ textTransform: "none" }}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                size="small"
                onClick={() => setAdtPackages((prev) => [...prev, blankAdtPackage()])}
                disabled={integrationReadOnly}
                sx={{ textTransform: "none", mt: 1.5 }}
              >
                + Add package
              </Button>
            </Box>

            <Box>
              <Typography fontWeight={700} mb={1.5}>Monthly Service Add-ons</Typography>
              <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
                {adtAddons.map((row) => (
                  <Box
                    key={row.id}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}
                  >
                    {row.locked ? (
                      <Typography fontWeight={700} sx={{ mb: 0.5 }}>{row.name}</Typography>
                    ) : (
                      <TextField
                        size="small"
                        label="Add-on name"
                        value={row.name}
                        onChange={(e) => updateAdtRow(setAdtAddons, row.id, "name", e.target.value)}
                        disabled={integrationReadOnly}
                        fullWidth
                        sx={{ mb: 1.25 }}
                      />
                    )}
                    {row.locked ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>{row.price}</Typography>
                    ) : (
                      <TextField
                        size="small"
                        label="Price"
                        placeholder="$0.00"
                        value={row.price}
                        onChange={(e) => updateAdtRow(setAdtAddons, row.id, "price", e.target.value)}
                        onBlur={() => updateAdtRow(setAdtAddons, row.id, "price", formatAdtCurrency(row.price))}
                        disabled={integrationReadOnly}
                        inputProps={{ inputMode: "decimal" }}
                        fullWidth
                        sx={{ mb: 1.25 }}
                      />
                    )}
                    <TextField
                      size="small"
                      label="ADT Passthrough"
                      placeholder="$0.00"
                      value={row.adtPassthrough}
                      onChange={(e) => updateAdtRow(setAdtAddons, row.id, "adtPassthrough", e.target.value)}
                      onBlur={() => updateAdtRow(setAdtAddons, row.id, "adtPassthrough", formatAdtCurrency(row.adtPassthrough))}
                      disabled={integrationReadOnly}
                      inputProps={{ inputMode: "decimal" }}
                      fullWidth
                    />
                    {!row.locked && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => setAdtAddons((prev) => prev.filter((item) => item.id !== row.id))}
                        disabled={integrationReadOnly}
                        sx={{ textTransform: "none", mt: 1 }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))}
              </Stack>
              <Table size="small" sx={{ width: "67%", display: { xs: "none", md: "table" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Add-on Name</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>ADT Passthrough</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adtAddons.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ pl: 0 }}>
                        {row.locked ? row.name : (
                          <TextField
                            size="small"
                            placeholder="Add-on name"
                            value={row.name}
                            onChange={(e) => updateAdtRow(setAdtAddons, row.id, "name", e.target.value)}
                            disabled={integrationReadOnly}
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.locked ? row.price : (
                          <TextField
                            size="small"
                            placeholder="$0.00"
                            value={row.price}
                            onChange={(e) => updateAdtRow(setAdtAddons, row.id, "price", e.target.value)}
                            onBlur={() => updateAdtRow(setAdtAddons, row.id, "price", formatAdtCurrency(row.price))}
                            disabled={integrationReadOnly}
                            inputProps={{ inputMode: "decimal" }}
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="$0.00"
                          value={row.adtPassthrough}
                          onChange={(e) => updateAdtRow(setAdtAddons, row.id, "adtPassthrough", e.target.value)}
                          onBlur={() => updateAdtRow(setAdtAddons, row.id, "adtPassthrough", formatAdtCurrency(row.adtPassthrough))}
                          disabled={integrationReadOnly}
                          inputProps={{ inputMode: "decimal" }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right">
                        {!row.locked && (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() =>
                              setAdtAddons((prev) => prev.filter((item) => item.id !== row.id))
                            }
                            disabled={integrationReadOnly}
                            sx={{ textTransform: "none" }}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                size="small"
                onClick={() => setAdtAddons((prev) => [...prev, blankAdtAddon()])}
                disabled={integrationReadOnly}
                sx={{ textTransform: "none", mt: 1.5 }}
              >
                + Add Add-on
              </Button>
            </Box>

            <Box>
              <Typography fontWeight={700} mb={1.5}>Contract Terms</Typography>
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
                <TextField
                  size="small"
                  label="Months"
                  value={adtTermDraft}
                  onChange={(e) => setAdtTermDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddAdtTerm();
                    }
                  }}
                  disabled={integrationReadOnly}
                  inputProps={{ inputMode: "numeric", min: 1 }}
                  sx={{ width: 120 }}
                />
                <Button
                  size="small"
                  onClick={handleAddAdtTerm}
                  disabled={integrationReadOnly}
                  sx={{ textTransform: "none" }}
                >
                  + Add term
                </Button>
                {adtContractTerms.map((months) => (
                  <Chip
                    key={months}
                    label={adtTermLabel(months)}
                    onDelete={
                      integrationReadOnly
                        ? undefined
                        : () => setAdtContractTerms((prev) => prev.filter((term) => term !== months))
                    }
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography fontWeight={700} mb={1.5}>Activation Fees</Typography>
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
                <TextField
                  size="small"
                  label="Amount"
                  placeholder="$0.00"
                  value={adtFeeDraft}
                  onChange={(e) => setAdtFeeDraft(e.target.value)}
                  onBlur={() => setAdtFeeDraft((value) => formatAdtCurrency(value) || value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddAdtFee();
                    }
                  }}
                  disabled={integrationReadOnly}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ width: 120 }}
                />
                <Button
                  size="small"
                  onClick={handleAddAdtFee}
                  disabled={integrationReadOnly}
                  sx={{ textTransform: "none" }}
                >
                  + Add fee
                </Button>
                {adtActivationFees.map((fee) => (
                  <Chip
                    key={fee}
                    label={fee}
                    onDelete={
                      integrationReadOnly
                        ? undefined
                        : () => setAdtActivationFees((prev) => prev.filter((item) => item !== fee))
                    }
                  />
                ))}
              </Stack>
            </Box>

            <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
              <Button
                variant="contained"
                fullWidth={!isDesktop}
                onClick={handleSaveAdt}
                disabled={integrationReadOnly || savingAdt}
                sx={{ textTransform: "none" }}
              >
                Save Settings
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
      </Box>
    </Box>
      <Dialog
        open={Boolean(leavePrompt)}
        onClose={savingAdt ? undefined : dismissLeavePrompt}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Unsaved changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", lineHeight: 1.5 }}>
            You have unsaved changes. Save before leaving the page.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={dismissLeavePrompt}
            disabled={savingAdt}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveAndLeave}
            disabled={savingAdt}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            {savingAdt ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
</Box>
  );
}
